import type { Instant, LocalDate, Plan, PlanItem, UUID } from './index';
import { addCalendarDays, calendarDayDifference } from './local-date';
import {
  inclusiveRangeLength,
  mergeInclusiveRanges,
  missingInclusiveRanges,
  type InclusiveRange,
} from './progress';

export type PlanSubjectInput = {
  subjectId: UUID;
  totalUnits: number;
  weight: number;
  sortOrder: number;
  completedByRound: Readonly<Record<number, readonly InclusiveRange[]>>;
};

export type GeneratePlanInput = {
  examId: UUID;
  startDate: LocalDate;
  examDate?: LocalDate | null | undefined;
  timezone: string;
  targetRounds: number;
  studyWeekdays: readonly number[];
  excludedDates: readonly LocalDate[];
  maxDailyUnits?: number;
  subjects: readonly PlanSubjectInput[];
};

export type GeneratedPlanItem = Omit<PlanItem, 'id' | 'planId' | 'status'>;

export type PlanDraft = {
  input: GeneratePlanInput;
  availableDates: LocalDate[];
  totalUnits: number;
  items: GeneratedPlanItem[];
  dailyUnits: Readonly<Record<string, number>>;
};

export type PlanGenerationResult =
  | { feasible: true; draft: PlanDraft }
  | {
      feasible: false;
      reason: 'no_available_days' | 'daily_capacity';
      totalUnits: number;
      availableDays: number;
      requiredMinimumDailyUnits: number | null;
    };

type WorkCursor = {
  subject: PlanSubjectInput;
  ranges: Array<InclusiveRange & { round: number }>;
  rangeIndex: number;
  nextUnit: number;
  assigned: number;
};

function validatePlanInput(input: GeneratePlanInput): void {
  if (!Number.isInteger(input.targetRounds) || input.targetRounds < 1) {
    throw new RangeError('targetRounds must be a positive integer');
  }
  if (input.examDate && calendarDayDifference(input.startDate, input.examDate) < 0) {
    throw new RangeError('startDate must not be after examDate');
  }
  if (
    input.studyWeekdays.some((weekday) => !Number.isInteger(weekday) || weekday < 0 || weekday > 6)
  ) {
    throw new RangeError('studyWeekdays must use integers from 0 through 6');
  }
  if (
    input.maxDailyUnits !== undefined &&
    (!Number.isInteger(input.maxDailyUnits) || input.maxDailyUnits < 1)
  ) {
    throw new RangeError('maxDailyUnits must be a positive integer');
  }
  if (!input.examDate && input.maxDailyUnits === undefined)
    throw new RangeError('Daily units are required without an exam date');
  if (input.subjects.length === 0) throw new RangeError('At least one subject is required');
  for (const subject of input.subjects) {
    if (!Number.isInteger(subject.totalUnits) || subject.totalUnits < 1) {
      throw new RangeError('Subject totalUnits must be positive');
    }
    if (!Number.isFinite(subject.weight) || subject.weight <= 0) {
      throw new RangeError('Subject weight must be positive');
    }
  }
}

function utcWeekday(date: LocalDate): number {
  return new Date(`${date}T00:00:00.000Z`).getUTCDay();
}

function availableDates(input: GeneratePlanInput): LocalDate[] {
  const allowed = new Set(input.studyWeekdays);
  const excluded = new Set(input.excludedDates);
  const result: LocalDate[] = [];
  if (!input.examDate) {
    if (!allowed.size) return [];
    const total = createWorkCursors(input).reduce((sum, cursor) => sum + remainingUnits(cursor), 0);
    const needed = Math.ceil(total / input.maxDailyUnits!);
    for (let offset = 0; result.length < needed && offset < 366_000; offset += 1) {
      const date = addCalendarDays(input.startDate, offset);
      if (allowed.has(utcWeekday(date)) && !excluded.has(date)) result.push(date);
    }
    return result;
  }
  const daysUntilExam = calendarDayDifference(input.startDate, input.examDate);
  for (let offset = 0; offset < daysUntilExam; offset += 1) {
    const date = addCalendarDays(input.startDate, offset);
    if (allowed.has(utcWeekday(date)) && !excluded.has(date)) result.push(date);
  }
  return result;
}

function createWorkCursors(input: GeneratePlanInput): WorkCursor[] {
  return [...input.subjects]
    .sort(
      (left, right) =>
        left.sortOrder - right.sortOrder || left.subjectId.localeCompare(right.subjectId),
    )
    .map((subject) => {
      const ranges: Array<InclusiveRange & { round: number }> = [];
      for (let round = 1; round <= input.targetRounds; round += 1) {
        const completed = mergeInclusiveRanges(subject.completedByRound[round] ?? []);
        for (const missing of missingInclusiveRanges(completed, subject.totalUnits)) {
          ranges.push({ ...missing, round });
        }
      }
      return {
        subject,
        ranges,
        rangeIndex: 0,
        nextUnit: ranges[0]?.start ?? 0,
        assigned: 0,
      };
    });
}

function remainingUnits(cursor: WorkCursor): number {
  return cursor.ranges.slice(cursor.rangeIndex).reduce((sum, range, index) => {
    const start = index === 0 ? cursor.nextUnit : range.start;
    return sum + Math.max(0, range.end - start + 1);
  }, 0);
}

function takeUnit(cursor: WorkCursor): { subjectId: UUID; round: number; unit: number } {
  const range = cursor.ranges[cursor.rangeIndex];
  if (!range) throw new RangeError('No plan work remains');
  const unit = cursor.nextUnit;
  cursor.assigned += 1;
  if (unit >= range.end) {
    cursor.rangeIndex += 1;
    cursor.nextUnit = cursor.ranges[cursor.rangeIndex]?.start ?? 0;
  } else {
    cursor.nextUnit += 1;
  }
  return { subjectId: cursor.subject.subjectId, round: range.round, unit };
}

function dayQuotas(totalUnits: number, dayCount: number): number[] {
  return Array.from(
    { length: dayCount },
    (_, index) =>
      Math.floor(((index + 1) * totalUnits) / dayCount) -
      Math.floor((index * totalUnits) / dayCount),
  );
}

function appendUnit(
  items: GeneratedPlanItem[],
  date: LocalDate,
  allocation: { subjectId: UUID; round: number; unit: number },
): void {
  const previous = items.findLast(
    (item) =>
      item.date === date &&
      item.subjectId === allocation.subjectId &&
      item.round === allocation.round,
  );
  if (
    previous &&
    previous.date === date &&
    previous.subjectId === allocation.subjectId &&
    previous.round === allocation.round &&
    previous.unitEnd + 1 === allocation.unit
  ) {
    previous.unitEnd = allocation.unit;
    return;
  }
  items.push({
    date,
    subjectId: allocation.subjectId,
    round: allocation.round,
    unitStart: allocation.unit,
    unitEnd: allocation.unit,
  });
}

export function generatePlan(input: GeneratePlanInput): PlanGenerationResult {
  validatePlanInput(input);
  const dates = availableDates(input);
  const cursors = createWorkCursors(input);
  const totalUnits = cursors.reduce((sum, cursor) => sum + remainingUnits(cursor), 0);
  if (totalUnits === 0) {
    return {
      feasible: true,
      draft: { input, availableDates: dates, totalUnits, items: [], dailyUnits: {} },
    };
  }
  if (dates.length === 0) {
    return {
      feasible: false,
      reason: 'no_available_days',
      totalUnits,
      availableDays: 0,
      requiredMinimumDailyUnits: null,
    };
  }
  const requiredMinimumDailyUnits = Math.ceil(totalUnits / dates.length);
  if (input.maxDailyUnits !== undefined && totalUnits > input.maxDailyUnits * dates.length) {
    return {
      feasible: false,
      reason: 'daily_capacity',
      totalUnits,
      availableDays: dates.length,
      requiredMinimumDailyUnits,
    };
  }

  const items: GeneratedPlanItem[] = [];
  const dailyUnits: Record<string, number> = {};
  const quotas = dayQuotas(totalUnits, dates.length);
  for (const [dateIndex, date] of dates.entries()) {
    const quota = quotas[dateIndex] ?? 0;
    dailyUnits[date] = quota;
    for (let used = 0; used < quota; used += 1) {
      const candidates = cursors.filter((cursor) => remainingUnits(cursor) > 0);
      candidates.sort((left, right) => {
        const fairness =
          left.assigned / left.subject.weight - right.assigned / right.subject.weight;
        return fairness || left.subject.sortOrder - right.subject.sortOrder;
      });
      const selected = candidates[0];
      if (!selected) throw new RangeError('Plan allocator exhausted before quota');
      appendUnit(items, date, takeUnit(selected));
    }
  }

  return {
    feasible: true,
    draft: { input, availableDates: dates, totalUnits, items, dailyUnits },
  };
}

export function materializePlanVersion(
  draft: PlanDraft,
  options: {
    planId: UUID;
    itemId: (index: number) => UUID;
    generatedAt: Instant;
    confirmedAt: Instant;
    previous?: Plan;
    history?: readonly Plan[];
  },
): { active: Plan; items: PlanItem[]; superseded?: Plan } {
  if (options.previous && options.previous.examId !== draft.input.examId) {
    throw new RangeError('Previous plan belongs to another exam');
  }
  const version =
    Math.max(
      options.previous?.version ?? 0,
      0,
      ...(options.history ?? [])
        .filter((plan) => plan.examId === draft.input.examId)
        .map((plan) => plan.version),
    ) + 1;
  const active: Plan = {
    id: options.planId,
    examId: draft.input.examId,
    version,
    status: 'active',
    params: {
      timezone: draft.input.timezone,
      targetRounds: draft.input.targetRounds,
      studyWeekdays: [...draft.input.studyWeekdays],
      excludedDates: [...draft.input.excludedDates],
      ...(draft.input.maxDailyUnits === undefined
        ? {}
        : { maxDailyUnits: draft.input.maxDailyUnits }),
      subjectWeights: Object.fromEntries(
        draft.input.subjects.map((subject) => [subject.subjectId, subject.weight]),
      ),
    },
    generatedAt: options.generatedAt,
    confirmedAt: options.confirmedAt,
    ...(options.previous ? { supersedesId: options.previous.id } : {}),
  };
  const items = draft.items.map<PlanItem>((item, index) => ({
    ...item,
    id: options.itemId(index),
    planId: active.id,
    status: 'planned',
  }));
  return {
    active,
    items,
    ...(options.previous ? { superseded: { ...options.previous, status: 'superseded' } } : {}),
  };
}

export function plannedUnitCount(items: readonly GeneratedPlanItem[]): number {
  return items.reduce(
    (sum, item) => sum + inclusiveRangeLength([{ start: item.unitStart, end: item.unitEnd }]),
    0,
  );
}

/** Compares schedules after subtracting recorded ranges, without changing either plan. */
export function comparePlanDraft(draft: PlanDraft, previousItems: readonly GeneratedPlanItem[]) {
  const subjects = new Map(draft.input.subjects.map((subject) => [subject.subjectId, subject]));
  const beforeDaily: Record<string, number> = {};
  for (const item of previousItems) {
    const subject = subjects.get(item.subjectId);
    if (!subject) continue;
    const missing = missingInclusiveRanges(
      subject.completedByRound[item.round] ?? [],
      subject.totalUnits,
    );
    const units = missing.reduce(
      (sum, range) =>
        sum +
        Math.max(0, Math.min(range.end, item.unitEnd) - Math.max(range.start, item.unitStart) + 1),
      0,
    );
    beforeDaily[item.date] = (beforeDaily[item.date] ?? 0) + units;
  }
  const dates = [...new Set([...Object.keys(beforeDaily), ...draft.availableDates])]
    .filter((date) => date >= draft.input.startDate)
    .sort() as LocalDate[];
  const nextDate =
    dates.find(
      (date) =>
        date > draft.input.startDate &&
        ((beforeDaily[date] ?? 0) > 0 || (draft.dailyUnits[date] ?? 0) > 0),
    ) ?? dates[0];
  return {
    nextDate,
    beforeNextUnits: nextDate ? (beforeDaily[nextDate] ?? 0) : 0,
    afterNextUnits: nextDate ? (draft.dailyUnits[nextDate] ?? 0) : 0,
    beforePeak: Math.max(0, ...dates.map((date) => beforeDaily[date] ?? 0)),
    afterPeak: Math.max(0, ...Object.values(draft.dailyUnits)),
    overdueUnits: Object.entries(beforeDaily).reduce(
      (sum, [date, units]) => sum + (date < draft.input.startDate ? units : 0),
      0,
    ),
  };
}
